"use client"

import { HomePageCarStripDto } from "api"
import { trackModelSliderHpClicks } from "common/analytics"
import useShopUrl from "common/bit/hooks/useShopUrl"
import NextImage from "common/bit/NextImage"
import NextLink from "common/bit/NextLink"
import slugify from "common/bit/utils/slugify"
import { Dispatch, SetStateAction } from "react"
import styled, { css } from "styled-components"
import SwiperCore, { A11y, Controller, Keyboard, Parallax } from "swiper"
import { Swiper, SwiperSlide } from "swiper/react"

const StyledSwiperWrapper = styled.div<{ $isshop?: boolean }>`
  width: 100%;
  display: flex;
  ${(p) => p.theme.breakpoints.up("md")} {
    flex-grow: 1;
    ${(p) =>
      p.$isshop &&
      css`
        /* Base margin calculation: as viewport height decreases, margin increases */
        margin-top: calc(-20vh - (1000px - 100vh) * 0.15);
        margin-bottom: calc(-12vh - (1000px - 100vh) * 0.15);
      `}
  }
`
const StyledSwiperJs = styled(Swiper).attrs({})`
  width: 100%;
`
const StyledSwiperSlide = styled(SwiperSlide)`
  width: max-content;
  box-sizing: border-box;
  height: max-content;
  display: flex;
  justify-content: center;
  ${(p) => p.theme.breakpoints.up("md")} {
    height: auto;
  }
`
const StyledImageWrapper = styled.div<{
  $isActive: boolean
  $isshop: boolean
  $isInShop: boolean
}>`
  position: relative;
  aspect-ratio: 16 / 9;
  height: auto;
  width: 100%;
  min-height: 100px;
  transform: ${({ $isActive }) =>
    $isActive ? "scale(1) translateY(0px)" : "scale(0.5) translateY(-20%)"};
  z-index: ${({ $isActive }) => ($isActive ? "2" : "1")};
  transition: transform 0.3s linear;
  ${(p) => p.theme.breakpoints.up("md")} {
    transform: ${({ $isActive }) => ($isActive ? "scale(1)" : "scale(0.7)")};
    ${(p) =>
      p.$isshop &&
      css`
        @media (max-height: 750px) {
          width: 550px;
          margin-bottom: -50px;
        }
        @media (max-height: 667px) {
          width: 400px;
        }
        @media (max-height: 580px) {
          width: 350px;
        }
      `}
  }
  ${(p) => p.theme.breakpoints.down("sm")} {
    min-height: 150px;
    ${(p) =>
      p.$isshop &&
      css`
        min-height: 200px;
      `}
  }
  ${(p) =>
    p.$isshop &&
    !p.$isInShop &&
    css`
      opacity: 0.6;
    `}
`

const StyledLinkReplacement = styled.div`
  position: static;
  display: inline;
`

type ColorImageSwiperProps = {
  carsStrip: HomePageCarStripDto[]
  setActiveIndex: Dispatch<SetStateAction<number>>
  colorIndex: number
  controlledSwiper?: SwiperCore
  setControlledSwiper?: Dispatch<SetStateAction<SwiperCore>>
  initialSlide?: number
  isshop?: boolean
}

const ColorImageSwiper = ({
  carsStrip,
  setActiveIndex,
  colorIndex,
  setControlledSwiper,
  controlledSwiper,
  isshop,
}: ColorImageSwiperProps) => {
  const initialSlide = 0

  const getCarLink = (card) => {
    const futureModel = card?.carState === "Future"
    const carLink = futureModel
      ? card.link
      : `/model/[modelId]/[modelSlug]`
          .replace("[modelId]", card.id.toString())
          .replace("[modelSlug]", slugify(card.name))
    return carLink
  }

  return (
    <StyledSwiperWrapper $isshop={isshop}>
      <StyledSwiperJs
        modules={[A11y, Keyboard, Parallax, Controller]}
        loop
        parallax
        initialSlide={initialSlide}
        allowTouchMove
        grabCursor
        centeredSlides
        slideToClickedSlide
        resizeObserver
        onSlideChange={(swiper) => {
          setActiveIndex(swiper.realIndex)
        }}
        onSwiper={setControlledSwiper}
        controller={{ control: controlledSwiper || undefined, by: "slide" }}
        keyboard={{
          enabled: true,
        }}
        breakpoints={{
          0: {
            slidesPerView: 1.5,
            spaceBetween: -100,
          },
          // when mui md up:
          960: {
            slidesPerView: 2.1,
          },
        }}
        init={false}
      >
        {carsStrip.map((data, index) => {
          // eslint-disable-next-line react-hooks/rules-of-hooks
          const carLink = useShopUrl(carsStrip[index]?.id)
          const LinkWrap =
            !data?.isInShop && isshop ? StyledLinkReplacement : NextLink
          return (
            <StyledSwiperSlide key={`swiperr-${index}`}>
              {({ isActive }) => {
                return (
                  <StyledImageWrapper
                    $isActive={isActive}
                    $isshop={!!isshop}
                    $isInShop={data?.isInShop}
                  >
                    <LinkWrap
                      href={isshop ? carLink : getCarLink(data)}
                      onClick={() => trackModelSliderHpClicks()}
                    >
                      <NextImage
                        image={{
                          imageUrl: data.modelColors[colorIndex].image.imageUrl,
                          alt: data.modelColors[colorIndex].image.alt,
                        }}
                        imageSizes={["100vw", "50vw"]}
                        cover={false}
                        priority={isActive}
                      />
                    </LinkWrap>
                  </StyledImageWrapper>
                )
              }}
            </StyledSwiperSlide>
          )
        })}
      </StyledSwiperJs>
    </StyledSwiperWrapper>
  )
}

export default ColorImageSwiper
